<template>
  <div class="disclosure">
    <div class="pairs">
      <div class="grid">
        <div class="label">Days in Outbreak</div>
        <div>{{ record.daysInOutbreak }}</div>
        <div class="label">Total # Cases</div>
        <div>{{ record.totalCases }}</div>
        <div class="label"># Resident Cases</div>
        <div>{{ record.residentCases }}</div>
        <div class="label"># Staff Cases</div>
        <div>{{ record.staffCases }}</div>
      </div>
      <div class="grid">
        <div class="label"># Active Cases</div>
        <div>{{ record.activeCases }}</div>
        <div class="label"># Active Resident Cases</div>
        <div>{{ record.residentActiveCases }}</div>
        <div class="label"># Active Staff Cases</div>
        <div>{{ record.staffActiveCases }}</div>
        <div class="label"># Deaths</div>
        <div>{{ record.deathCases }}</div>
        <div class="label"># Hospitalizations</div>
        <div>{{ record.hospitalizedCases }}</div>
      </div>
      <div class="grid">
        <div class="label">Current Staff Census</div>
        <div>{{ staffCesus }}</div>
        <div class="label" style="grid-column: 1/-1;">Current Resident Census</div>
        <div>
          <template v-if="residentCesus.length">
            <div class="unit" v-for="census in residentCesus" :key="census">&#8226; {{ census }}</div>
          </template>
          <template v-else>
            Loading...
          </template>
        </div>
      </div>
      <div class="grid" v-if="vaccinationCounts.length">
        <template v-for="count in vaccinationCounts">
          <div class="label" :key="`${count.label}-label`">{{ count.label }}</div>
          <div :key="`${count.label}-value`">{{ count.value }}</div>
        </template>
      </div>
      <div class="grid">
        <div class="label">Created By</div>
        <div>{{ record.createdBy }}</div>
        <div class="label">Created Date</div>
        <div>{{ record.createdDateFormatted }}</div>
        <div class="label">Updated By</div>
        <div>{{ record.updatedBy }}</div>
        <div class="label">Updated Date</div>
        <div>{{ record.updatedDateFormatted }}</div>
      </div>
    </div>
    <div class="grid notes compliance" v-if="showHandHygieneCompliance || showPpeCompliance">
      <template v-if="showHandHygieneCompliance">
        <strong>Hand Hygiene Compliance</strong>
        <span v-if="hhCompliance.loading">Loading...</span>
        <span v-else-if="hhCompliance.total === 0">No observations</span>
        <span v-else>{{ hhCompliance.percent }}% ({{ hhCompliance.compliant }}/{{ hhCompliance.total }})</span>
      </template>
      <template v-if="showPpeCompliance">
        <strong>PPE Use Compliance</strong>
        <span v-if="ppeCompliance.loading">Loading...</span>
        <span v-else-if="ppeCompliance.total === 0">No observations</span>
        <span v-else>{{ ppeCompliance.percent }}% ({{ ppeCompliance.compliant }}/{{ ppeCompliance.total }})</span>
      </template>
      <strong>Case Definition</strong> <span>{{ record.caseDefinition }}</span>
      <strong>Outbreak Notes</strong> <span>{{ record.notes }}</span>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import auth from "../../auth";
import { visibleVaccinationCounts } from "./vaccinationStatusFields";

const ROLE_ADMIN_STAFF = "ROLE_ADMIN_STAFF";

export default {
  name: "Disclosure",
  props: ["record"],
  watch: {
    record: function (n, o) {
      this.renderContent();
      this.loadCompliance();
    }
  },
  data() {
    return {
      sites: [],
      units: [],
      staffCesus: "Loading...",
      residentCesus: [],
      hhCompliance: { loading: true, total: 0, compliant: 0, percent: 0 },
      ppeCompliance: { loading: true, total: 0, compliant: 0, percent: 0 }
    };
  },
  computed: {
    showHandHygieneCompliance() {
      return this.$configStore.hhModuleEnabled();
    },
    showPpeCompliance() {
      return this.$configStore.ppeModuleEnabled();
    },
    vaccinationCounts() {
      return visibleVaccinationCounts(this.record, this.isAdminStaff());
    }
  },
  methods: {
    isAdminStaff() {
      return auth.userInfo().roles.indexOf(ROLE_ADMIN_STAFF) >= 0;
    },
    // Compliance stats are compiled from (first case date - 3 days) through today,
    // or through the resolution date when the outbreak is resolved.
    getComplianceWindow() {
      const startBasis = this.record.firstCaseDate || this.record.declaredDate;
      const start = moment(startBasis).subtract(3, "days").startOf("day");
      const end = this.record.resolvedDate
        ? moment(moment.utc(this.record.resolvedDate).format("YYYY-MM-DD")).endOf("day")
        : moment();
      return { start, end };
    },
    loadCompliance() {
      if (!this.record.facId) {
        return;
      }
      if (this.showHandHygieneCompliance) {
        this.getHandHygieneCompliance();
      }
      if (this.showPpeCompliance) {
        this.getPpeCompliance();
      }
    },
    async getHandHygieneCompliance() {
      this.hhCompliance.loading = true;
      const { start, end } = this.getComplianceWindow();
      const params = {
        facilityIds: [this.record.facId],
        start: start.toDate().toString(),
        end: end.toDate().toString(),
        active: true,
        hhOutcomeCriteria: this.$configStore.hhOutcomeCriteria(),
        groupBy: "moment",
        timeScale: "week",
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      const response = await this.$http
        .get(`${window.CONFIG.hh_api}/summary`, { params })
        .then(resp => resp.json())
        .catch(() => []);
      const totals = response.reduce(
        (acc, r) => {
          acc.total += r.total;
          if (r.status === "complete") {
            acc.compliant += r.total;
          }
          return acc;
        },
        { total: 0, compliant: 0 }
      );
      this.hhCompliance.total = totals.total;
      this.hhCompliance.compliant = totals.compliant;
      this.hhCompliance.percent = totals.total === 0 ? 0 : Math.round((totals.compliant / totals.total) * 1000) / 10;
      this.hhCompliance.loading = false;
    },
    async getPpeCompliance() {
      this.ppeCompliance.loading = true;
      const { start, end } = this.getComplianceWindow();
      const filters = {
        filters: {
          recordedLocalDatetimeStart: start.format(),
          recordedLocalDatetimeEnd: end.format(),
          includeDeleted: false,
          facilityIds: [this.record.facId]
        },
        groupBy: ["sectionName", "week"],
        hhOutcomeCriteria: this.$configStore.hhOutcomeCriteria()
      };
      const response = await this.$http
        .post(`${window.CONFIG.ppe_api}/audit/summary`, filters)
        .then(resp => resp.json())
        .catch(() => ({ incomplete: [], yes: [], no: [] }));
      const sumCounts = arr => (arr || []).reduce((acc, r) => acc + r.count, 0);
      const yes = sumCounts(response.yes);
      const incomplete = sumCounts(response.incomplete);
      const no = sumCounts(response.no);
      const total = yes + incomplete + no;
      const compliant = yes + incomplete;
      this.ppeCompliance.total = total;
      this.ppeCompliance.compliant = compliant;
      this.ppeCompliance.percent = total === 0 ? 0 : Math.round((compliant / total) * 1000) / 10;
      this.ppeCompliance.loading = false;
    },
    // component methods
    async renderContent() {
      if (!this.record.facId) {
        return;
      }
      const staffResponse = await this.$http
        .get(`${window.CONFIG.client_api}`, {
          params: {
            facIds: [this.record.facId],
            status: "C",
            clientType: "Staff"
          }
        })
        .then(resp => resp.json());
      this.staffCesus = staffResponse.totalElements;
      let scopeUnits = this.units.filter(u => u.siteId === this.record.facId);
      if (this.record.scope === "Unit") {
        scopeUnits = scopeUnits.filter(u => this.record.scopeDetail.includes(u.displayText))
      }
      const unitStats = await Promise.all(
        scopeUnits.map(unit => {
          return this.$http
            .get(`${window.CONFIG.client_api}`, {
              params: {
                facIds: [this.record.facId],
                unitIds: [unit.id],
                status: "C",
                clientType: "Client"
              }
            })
            .then(resp => resp.json());
        })
      ).then(unitResults => {
        return Object.fromEntries(
          unitResults.map((response, i) => {
            return [scopeUnits[i].displayText, response.totalElements];
          })
        );
      });
      this.residentCesus = Object.entries(unitStats)
        .map(([k, v]) => `${k} = ${v}`)
    }
  },
  async created() {
    this.sites = this.$configStore.data.sites || [];
    this.units = this.$configStore.data.units || [];
  },
  mounted() {
    this.renderContent();
    this.loadCompliance();
  }
};
</script>

<style scoped>
.disclosure {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pairs {
  display: grid;
  align-items: start;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  margin: 0;
  gap: 0.25rem;
}

.grid {
  display: grid;
  grid-template-columns: max-content max-content;
  margin: 0;
  gap: 0.25rem 1rem;
}

.grid .label {
  font-weight: bold;
}

.grid.notes {
  grid-template-columns: max-content 1fr;
}

/* The label/value groups are wider than a printed page in a single grid row —
   let them wrap so nothing is clipped at the print frame edge. */
@media print {
  .pairs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 2rem;
  }
}
</style>
